///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2026
//                                    RXO Inc.
//                         All Rights Reserved Worldwide
//
// WARNING:  This program (or document) is unpublished, proprietary
// property of RXO Inc. and is to be maintained in strict confidence.
// Unauthorized reproduction, distribution or disclosure of this program
// (or document), or any program (or document) derived from it is
// prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useRef, useLayoutEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";

import Tooltip from "./Tooltip";
import BidTags from "./BidTags";
import AccessTimeIcon from "./icons/AccessTimeIcon";
import getLaneCount from "../helpers/getLaneCount";
import getDeadlineDate from "../helpers/getDeadlineDate";
import getStartDate from "../helpers/getStartDate";
import getLastStatusChangeDate from "../helpers/getLastStatusChangeDate";
import messages from "../helpers/messages";
import getActiveBidAvatars from "../helpers/getActiveBidAvatars";
import { status as bidStatus } from "../helpers/types";

const ActiveBid = ({ bid, status }) => {
  const { user } = useSelector(state => state.user);
  const bidNameRef = useRef(null);
  const shipperNameRef = useRef(null);
  const [bidNameOverflows, setBidNameOverflows] = useState(false);
  const [shipperNameOverflows, setShipperNameOverflows] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const isFeedback = status === bidStatus.feedback;
  const formattedDate = isFeedback
    ? getStartDate(bid, user, timeZone)
    : getDeadlineDate(bid, user, timeZone);
  const dateMessage = isFeedback
    ? messages.rateEffectiveDateWithFormattedDate
    : messages.pricingDueDate;
  const lastStatusChangeTime = getLastStatusChangeDate(bid, user, timeZone);
  const activeBidAvatars = getActiveBidAvatars(bid);
  const maxAvatars = 6;

  useLayoutEffect(() => {
    const bidNameOverflows =
      bidNameRef.current.offsetWidth < bidNameRef.current.scrollWidth;
    setBidNameOverflows(bidNameOverflows);
    setShipperNameOverflows(
      shipperNameRef.current.offsetWidth < shipperNameRef.current.scrollWidth
    );
    const updateWindowWidth = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", updateWindowWidth);
    return () => window.removeEventListener("resize", updateWindowWidth);
  }, [bidNameRef, windowWidth, shipperNameRef]);

  return (
    <Link className="decoration-none m-0 p-0" to={`/bid/${bid.bidId}/summary`}>
      <dd className="bid px-12 pb-8">
        <div className="bid-card card card--with-rounded-corners pos-relative">
          {bidNameOverflows ? (
            <Tooltip content={bid.bidName} offset={[-6, 8]}>
              <p className="bid-name mb-4 truncate font-bold" ref={bidNameRef}>
                {bid.bidName}
              </p>
            </Tooltip>
          ) : (
            <p className="bid-name mb-4 truncate font-bold" ref={bidNameRef}>
              {bid.bidName}
            </p>
          )}
          {shipperNameOverflows ? (
            <Tooltip content={bid.shipperName} offset={[-6, 12]}>
              <p className="shipper-name mb-4 truncate" ref={shipperNameRef}>
                {bid.shipperName}
              </p>
            </Tooltip>
          ) : (
            <p className="shipper-name mb-4 truncate" ref={shipperNameRef}>
              {bid.shipperName}
            </p>
          )}
          <div className="bid-avatars py-2">
            {activeBidAvatars.slice(0, maxAvatars)}
            {activeBidAvatars.length > maxAvatars && "..."}
          </div>
          <p className="bid-deadline-date mb-0 nowrap py-4">
            <FormattedMessage
              {...dateMessage}
              values={{ formattedDate }}
            />
          </p>
          <div className="bid-tag text-right pt-2">
            <BidTags bid={bid} user={user} />
          </div>
          <p className="bid-lane-count mb-0">
            <FormattedMessage
              {...messages.laneCount}
              values={{ noOfLanes: getLaneCount(bid.modes) }}
            />
          </p>
          <p className="bid-last-status-change-time mb-0 pt-2">
            <AccessTimeIcon
              className="align-middle"
              height={12}
              width={12}
              fill="#666"
              title={messages.timeSinceLastUpdate.defaultMessage}
            />
            <span className="inline-block align-middle ml-4 color-grey">
              <FormattedMessage
                {...messages.timeSinceLastUpdateDuration}
                values={{ timeSinceLastUpdateDuration: lastStatusChangeTime }}
              />
            </span>
          </p>
        </div>
      </dd>
    </Link>
  );
};

export default ActiveBid;
